import loadable from "@loadable/component";
import { inject } from "mobx-react";
import * as React from "react";
import { ResponsiveImage } from "../components/ResponsiveImage.component";
import type { ImageAssetInterface } from "@ihr-radioedit/inferno-core";
import { getMediaServerUrl, AssetProps } from "@ihr-radioedit/inferno-core";
import { generateImageAssetSizes, iHeartDefaultImg } from "@inferno/renderer-shared-core";
import { placeholderImage } from "@ihr-radioedit/inferno-core";
import "./Asset.style.scss";
import { RESPONSIVE_IMAGE } from "../ui/constants";

const DocumentAsset = loadable(() => import("./DocumentAsset.component"));
const ImageAsset = loadable(() => import("./ImageAsset.component"));
const MediaAsset = loadable(() => import("./MediaAsset.component"));

export interface AssetState {
  visible: boolean;
}

@inject("store")
export class Asset extends React.Component<AssetProps, AssetState> {
  private observer?: IntersectionObserver;
  private elementRef: React.RefObject<HTMLElement>;
  private imageHost: string;
  mounted = false;

  constructor(props: AssetProps) {
    super(props);
    this.state = {
      visible: false,
    };

    this.imageHost = this.props?.store?.env ? this.props?.store.env.IMAGE_HOST || "" : "";
    this.elementRef = React.createRef();
  }

  private handleIntersect = (entries: IntersectionObserverEntry[]) => {
    const [item] = entries;
    if (item && item.intersectionRatio > 0.5 && this.mounted) {
      this.setState({
        visible: true,
      });
      if (this.observer) {
        this.observer.disconnect();
      }
    }
  };

  componentDidMount() {
    this.mounted = true;
    if (!this.elementRef.current) {
      return;
    }
    const options = {
      rootMargin: "0px 0px 0px 0px",
      threshold: [0, 0.25, 0.5, 0.75, 1],
    };

    this.observer = new IntersectionObserver(e => this.handleIntersect(e), options);
    this.observer.observe(this.elementRef.current);
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  getBlock = () => {
    const { attributes, title, bucket, id, link, new_tab, caption, alt_text, source_credit, counter, use_caption } =
      this.props.value;
    if (!attributes) {
      return null;
    }

    switch (attributes.type) {
      case "image":
        const imageProps: ImageAssetInterface = {
          url: link ?? "",
          title,
          imgUrl: getMediaServerUrl(this.imageHost, bucket, id),
          new_tab,
          caption,
          alt_text,
          source_credit,
          counter,
          use_caption,
        };
        return <ImageAsset {...imageProps} />;
      case "document":
        return <DocumentAsset imageHost={this.imageHost} asset={this.props.value} />;
      case "video":
      case "audio":
        return <MediaAsset {...this.props.value} />;
      default:
        return <div className="no-content" />;
    }
  };

  getPlaceholder = () => {
    const { attributes, title } = this.props.value;
    if (!attributes) {
      return null;
    }

    let imgUrl = "";
    switch (attributes.type) {
      case "image":
        imgUrl = placeholderImage;
        break;
      case "video":
        imgUrl = iHeartDefaultImg;
        break;
      case "audio":
        imgUrl = iHeartDefaultImg;
        break;
      case "document":
        imgUrl = iHeartDefaultImg;
        break;
    }

    const { srcset, sizes, placeholderSrcset } = generateImageAssetSizes(imgUrl);
    const alt = title || "placeholder image";

    return (
      <figure ref={this.elementRef} className="component-asset-placeholder" data-placeholder-type={attributes.type}>
        <section className="image-container">
          <ResponsiveImage
            src={placeholderImage}
            srcset={srcset}
            sizes={sizes}
            alt={alt}
            placeholderSrcset={placeholderSrcset}
            initialWidth={RESPONSIVE_IMAGE.responsiveImageInitialWidth}
            initialHeight={RESPONSIVE_IMAGE.responsiveImageInitialHeight}
          />
        </section>
      </figure>
    );
  };

  render() {
    const { attributes } = this.props.value;
    if (!attributes) {
      return null;
    }

    const validAsset = attributes && (attributes.type === "image" || attributes.type === "document");
    if (this.state.visible || validAsset) {
      return this.getBlock();
    } else {
      return this.getPlaceholder();
    }
  }
}

export default Asset;
